// PdfPreview — full, scrollable, readable PDF preview for the upload modal.
//
// Unlike PdfThumbnail (which rasterizes only page 1 for a card/thumbnail), this
// renders EVERY page to its own <canvas> stacked vertically inside a scrollable
// container, so the operator can scroll through and actually read the whole
// document before uploading. Accepts a local File (the pre-upload preview).
//
// pdf.js worker note: we spin up a dedicated PDFWorker per instance instead of a
// shared global workerPort. A shared port makes pdf.js resolve every instance to
// the same worker, so tearing one down ("the worker is being destroyed") can
// abort another that's mounted at the same time. Mirrors the webplayer's
// PdfDisplay fix.

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import * as pdfjsLib from 'pdfjs-dist'
// Vite ?worker virtual import — typed via vite/client.
import PdfJsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'

interface Props {
  file: File
  className?: string
}

export default function PdfPreview({ file, className }: Props) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    let task: ReturnType<typeof pdfjsLib.getDocument> | null = null
    // Dedicated worker for THIS instance only (see header note). pdfjs-dist
    // mistypes the ctor `port` as null|undefined, so cast through unknown.
    const worker = new pdfjsLib.PDFWorker(
      { port: new PdfJsWorker() } as unknown as { port?: null },
    )
    setStatus('loading')
    const container = containerRef.current
    if (container) container.replaceChildren()

    ;(async () => {
      try {
        const buffer = await file.arrayBuffer()
        if (cancelled) return
        task = pdfjsLib.getDocument({ data: new Uint8Array(buffer), worker })
        const doc = await task.promise
        if (cancelled) return
        // Fit each page to the container width; DPR-aware (capped at 2) so
        // hi-DPI screens stay crisp without an unbounded canvas footprint.
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        const cssWidth = container?.clientWidth || 800
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return
          const p = await doc.getPage(n)
          if (cancelled) return
          const base = p.getViewport({ scale: 1 })
          const viewport = p.getViewport({ scale: (cssWidth / base.width) * dpr })
          const canvas = document.createElement('canvas')
          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)
          // Full-width pages stacked top-to-bottom — the container scrolls.
          canvas.style.width = '100%'
          canvas.style.height = 'auto'
          canvas.style.display = 'block'
          canvas.className = 'shadow-sm rounded-sm mb-2 last:mb-0'
          container?.appendChild(canvas)
          await p.render({ canvas, viewport }).promise
        }
        if (!cancelled) setStatus('ready')
      } catch (e) {
        if (!cancelled) { console.error('PDF preview render failed', e); setStatus('error') }
      }
    })()

    return () => {
      cancelled = true
      // Tear the document down BEFORE terminating the worker (pdf.js asks for
      // this), then kill the worker so its thread + port are released.
      ;(async () => {
        try { await task?.destroy() } catch { /* noop */ }
        try { worker.destroy() } catch { /* noop */ }
      })()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file])

  return (
    <div className={`relative ${className ?? ''}`}>
      {status !== 'ready' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted/40">
          {status === 'error'
            ? <p className="text-xs text-muted-foreground px-4 text-center">{t('assets.pdfPreviewError', 'Preview unavailable.')}</p>
            : <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
        </div>
      )}
      {/* Scroll surface — every page rasterized full-width, top to bottom. */}
      <div ref={containerRef} className="absolute inset-0 overflow-y-auto overflow-x-hidden bg-zinc-200 dark:bg-zinc-800 p-2" />
    </div>
  )
}
